import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import AddBowlerModalAdmin from './AddBowlerModalAdmin';

const BACKEND_URL = process.env.REACT_APP_API_URL;

interface Bowler {
  id: number;
  name: string;
  email: string;
  playerId: number;
  joinedAt: string;
  leaguesCount: number;
  leagueNames: string;
}

interface PaginationInfo {
  currentPage: number;
  totalPages: number;
  totalBowlers: number;
  limit: number;
}

interface InviteBowlersModalProps {
  leagueId: number;
  leagueName: string;
  teamFormat: string;
  onClose: () => void;
}

const InviteBowlersModal: React.FC<InviteBowlersModalProps> = ({ leagueId, leagueName, teamFormat, onClose }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'invite' | 'proposeTeam'>('invite');
  const [bowlers, setBowlers] = useState<Bowler[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [error, setError] = useState<string>('');
  const [confirmingBowlerId, setConfirmingBowlerId] = useState<number | null>(null);
  const [invitingBowlerId, setInvitingBowlerId] = useState<number | null>(null);
  const [successMessage, setSuccessMessage] = useState<string>('');
  
  const [showAddBowlerModal, setShowAddBowlerModal] = useState(false);

  // Propose Team state
  const [selectedBowlers, setSelectedBowlers] = useState<Bowler[]>([]);
  const [teamName, setTeamName] = useState('');
  const [proposing, setProposing] = useState(false);
  const [forceRegistering, setForceRegistering] = useState(false);

  // Get max team size from format
  const getMaxTeamSize = () => {
    const format = teamFormat.toLowerCase();
    if (format.includes('singles') || format.includes('individual')) return 1;
    if (format.includes('doubles') || format.includes('duos') || format.includes('pairs')) return 2;
    if (format.includes('trios') || format.includes('three')) return 3;
    if (format.includes('4-person') || format.includes('four') || format.includes('quad')) return 4;
    if (format.includes('5-person') || format.includes('five')) return 5;
    return 5; // Default max
  };

  const maxTeamSize = getMaxTeamSize();

  const fetchBowlers = useCallback(async () => {
    try {
      setLoading(true);
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const searchParams = new URLSearchParams({
        page: currentPage.toString(),
        limit: '20'
      });

      if (searchTerm.trim()) {
        searchParams.append('search', searchTerm.trim());
      }

      const response = await fetch(`${apiUrl}/admin/invites/bowlers?${searchParams}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setBowlers(data.bowlers);
        setPagination(data.pagination);
        setError('');
      } else {
        setError(t('admin.inviteBowlers.fetchError'));
      }
    } catch (error) {
      console.error('Failed to fetch bowlers:', error);
      setError(t('admin.inviteBowlers.fetchError'));
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm, t]);

  useEffect(() => {
    fetchBowlers();
  }, [fetchBowlers]);

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleInviteClick = (bowlerId: number) => {
    setConfirmingBowlerId(bowlerId);
    setSuccessMessage('');
  };

  const handleConfirmInvite = async (bowlerId: number) => {
    try {
      setInvitingBowlerId(bowlerId);
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';

      const response = await fetch(`${apiUrl}/admin/invites/invite`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          bowlerId,
          leagueId
        })
      });

      if (response.ok) {
        const data = await response.json();
        setSuccessMessage(t('admin.inviteBowlers.inviteSent', { email: data.bowlerEmail }));
        setConfirmingBowlerId(null);
        
        // Clear success message after 5 seconds
        setTimeout(() => {
          setSuccessMessage('');
        }, 5000);
      } else {
        const errorData = await response.json();
        setError(errorData.error || t('admin.inviteBowlers.sendError'));
      }
    } catch (error) {
      console.error('Failed to send invitation:', error);
      setError(t('admin.inviteBowlers.sendError'));
    } finally {
      setInvitingBowlerId(null);
    }
  };

  const handleCancelConfirm = () => {
    setConfirmingBowlerId(null);
  };

  // Propose Team handlers
  const toggleBowlerSelection = (bowler: Bowler) => {
    if (selectedBowlers.find(b => b.id === bowler.id)) {
      setSelectedBowlers(selectedBowlers.filter(b => b.id !== bowler.id));
    } else {
      if (selectedBowlers.length < maxTeamSize) {
        setSelectedBowlers([...selectedBowlers, bowler]);
      }
    }
  };

  const handleProposeTeam = async () => {
    if (!teamName.trim()) {
      setError(t('admin.inviteBowlers.enterTeamName'));
      return;
    }

    if (selectedBowlers.length === 0) {
      setError(t('admin.inviteBowlers.selectAtLeastOne'));
      return;
    }

    try {
      setProposing(true);
      setError('');
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';

      const response = await fetch(`${apiUrl}/admin/invites/propose-team`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          teamName: teamName.trim(),
          leagueId,
          bowlerIds: selectedBowlers.map(b => b.id)
        })
      });

      if (response.ok) {
        const data = await response.json();
        setSuccessMessage(t('admin.inviteBowlers.proposalSent', { count: data.membersNotified }));
        setTeamName('');
        setSelectedBowlers([]);
        
        setTimeout(() => {
          setSuccessMessage('');
        }, 5000);
      } else {
        const errorData = await response.json();
        setError(errorData.error || t('admin.inviteBowlers.proposalFailed'));
      }
    } catch (error) {
      console.error('Failed to propose team:', error);
      setError(t('admin.inviteBowlers.proposalFailed'));
    } finally {
      setProposing(false);
    }
  };

  const handleForceRegisterTeam = async () => {
    if (!teamName.trim()) {
      setError(t('admin.inviteBowlers.enterTeamName'));
      return;
    }

    if (selectedBowlers.length === 0) {
      setError(t('admin.inviteBowlers.selectAtLeastOne'));
      return;
    }

    try {
      setForceRegistering(true);
      setError('');
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';

      const response = await fetch(`${apiUrl}/admin/invites/force-register-team`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          teamName: teamName.trim(),
          leagueId,
          bowlerIds: selectedBowlers.map(b => b.id)
        })
      });

      if (response.ok) {
        const data = await response.json();
        setSuccessMessage(t('admin.inviteBowlers.forceRegisterSuccess', { count: data.membersRegistered }));
        setTeamName('');
        setSelectedBowlers([]);
        
        setTimeout(() => {
          setSuccessMessage('');
        }, 5000);
      } else {
        const errorData = await response.json();
        if (errorData.alreadyCreated) {
          setError(t('admin.inviteBowlers.teamAlreadyExists'));
        } else if (errorData.alreadyInTeam) {
          setError(t('admin.inviteBowlers.bowlersAlreadyInTeam'));
        } else {
          setError(errorData.error || t('admin.inviteBowlers.forceRegisterFailed'));
        }
      }
    } catch (error) {
      console.error('Failed to force register team:', error);
      setError(t('admin.inviteBowlers.forceRegisterFailed'));
    } finally {
      setForceRegistering(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-50"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 max-w-4xl w-full mx-4 relative"
        style={{
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Header */}
        <div className="p-6 border-b border-white/20">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold text-white">{t('admin.inviteBowlers.title')}</h2>
              <p className="text-blue-300 text-sm mt-1">
                {t('admin.inviteBowlers.subtitle', { leagueName })}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-white/70 hover:text-white transition"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Tabs */}
          <div className="flex space-x-2 mt-4">
            <button
              onClick={() => setActiveTab('invite')}
              className={`px-4 py-2 rounded-lg font-medium transition ${
                activeTab === 'invite'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white/5 text-blue-300 hover:bg-white/10'
              }`}
            >
              📧 {t('admin.inviteBowlers.tabInviteIndividual')}
            </button>
            <button
              onClick={() => setActiveTab('proposeTeam')}
              className={`px-4 py-2 rounded-lg font-medium transition ${
                activeTab === 'proposeTeam'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white/5 text-blue-300 hover:bg-white/10'
              }`}
            >
              👥 {t('admin.inviteBowlers.tabProposeTeam')}
            </button>
          </div>

          {/* Search Bar + Add Bowler Button */}
          <div className="mt-4 flex items-center gap-2">
            <div className="relative flex-1">
              <input
                type="text"
                placeholder={t('admin.inviteBowlers.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                className="w-full bg-white/5 border border-white/20 rounded-lg px-3 py-1.5 pl-8 text-sm text-white placeholder-white/50 focus:outline-none focus:border-blue-400/50"
              />
              <svg
                className="w-4 h-4 text-white/50 absolute left-2.5 top-1/2 transform -translate-y-1/2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <button
              onClick={() => setShowAddBowlerModal(true)}
              className="flex-shrink-0 flex items-center gap-1.5 bg-emerald-600/40 hover:bg-emerald-600/70 border border-emerald-500/40 text-white text-xs font-medium px-3 py-1.5 rounded-lg transition duration-200 whitespace-nowrap"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
              </svg>
              Bowler not on system?
            </button>
          </div>

          {/* Team Name Input (Propose Team Tab) */}
          {activeTab === 'proposeTeam' && (
            <div className="mt-4">
              <label className="text-blue-200 text-sm font-medium mb-2 block">
                {t('admin.inviteBowlers.teamNameLabel')}
              </label>
              <input
                type="text"
                placeholder={t('admin.inviteBowlers.teamNamePlaceholder')}
                value={teamName}
                onChange={(e) => setTeamName(e.target.value)}
                className="w-full bg-white/5 border border-white/20 rounded-lg px-4 py-2 text-white placeholder-white/50 focus:outline-none focus:border-blue-400/50"
              />
              <p className="text-blue-300 text-xs mt-1">
                {teamFormat} - {t('admin.inviteBowlers.selectUpTo', { count: maxTeamSize })}
                {selectedBowlers.length > 0 && ` (${t('admin.inviteBowlers.selectedCount', { count: selectedBowlers.length, max: maxTeamSize })})`}
              </p>
            </div>
          )}

          {/* Success Message */}
          {successMessage && (
            <div className="mt-4 bg-green-500/20 border border-green-400/30 rounded-lg p-3">
              <p className="text-green-200 text-sm">{successMessage}</p>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mt-4 bg-red-500/20 border border-red-400/30 rounded-lg p-3">
              <p className="text-red-200 text-sm">{error}</p>
            </div>
          )}
        </div>

        {/* Bowlers List */}
        <div
          className="flex-1 overflow-y-auto p-6"
          style={{
            scrollbarWidth: 'thin',
            scrollbarColor: '#6366f1 #18181b'
          }}
        >
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="text-white">{t('common.loading')}</div>
            </div>
          ) : bowlers.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-4xl mb-2">🎳</div>
              <p className="text-blue-200">{t('admin.inviteBowlers.noBowlers')}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {bowlers.map((bowler) => {
                const isSelected = selectedBowlers.find(b => b.id === bowler.id) !== undefined;
                return (
                  <div
                    key={bowler.id}
                    className={`bg-white/5 rounded-lg p-4 transition ${
                      activeTab === 'proposeTeam'
                        ? isSelected
                          ? 'border-2 border-blue-500 bg-blue-500/10 cursor-pointer'
                          : 'border border-white/10 hover:border-blue-400/50 cursor-pointer hover:bg-white/10'
                        : 'border border-white/10 hover:bg-white/10'
                    }`}
                    onClick={() => activeTab === 'proposeTeam' && toggleBowlerSelection(bowler)}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex items-start space-x-3 flex-1">
                        {activeTab === 'proposeTeam' && (
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleBowlerSelection(bowler)}
                            className="w-5 h-5 mt-1 text-blue-600 bg-white/10 border-white/30 rounded focus:ring-2 focus:ring-blue-500"
                            onClick={(e) => e.stopPropagation()}
                          />
                        )}
                        <div className="flex-1">
                          <h3 className="text-white font-semibold text-lg">{bowler.name}</h3>
                          <p className="text-blue-300 text-sm">{bowler.email}</p>
                          <div className="flex items-center space-x-4 mt-2 text-sm">
                            <span className="text-blue-200">
                              {t('admin.inviteBowlers.leaguesCount', { count: bowler.leaguesCount })}
                            </span>
                            {bowler.leagueNames && (
                              <span className="text-blue-300 text-xs truncate max-w-xs">
                                {bowler.leagueNames}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Invite Button or Confirmation (only in invite tab) */}
                      {activeTab === 'invite' && (
                        <div className="ml-4">
                          {confirmingBowlerId === bowler.id ? (
                            <div className="flex flex-col space-y-2">
                              <p className="text-yellow-300 text-sm mb-1">{t('admin.inviteBowlers.areYouSure')}</p>
                              <div className="flex space-x-2">
                                <button
                                  onClick={() => handleConfirmInvite(bowler.id)}
                                  disabled={invitingBowlerId === bowler.id}
                                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-1 rounded text-sm font-medium transition disabled:opacity-50"
                                >
                                  {invitingBowlerId === bowler.id ? t('common.Processing') : t('common.yes')}
                                </button>
                                <button
                                  onClick={handleCancelConfirm}
                                  disabled={invitingBowlerId === bowler.id}
                                  className="bg-red-600 hover:bg-red-700 text-white px-4 py-1 rounded text-sm font-medium transition disabled:opacity-50"
                                >
                                  {t('common.no')}
                                </button>
                              </div>
                            </div>
                          ) : (
                            <button
                              onClick={() => handleInviteClick(bowler.id)}
                              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition"
                            >
                              {t('admin.inviteBowlers.inviteButton')}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer - Pagination or Propose Team Button */}
        {activeTab === 'invite' && pagination && pagination.totalPages > 1 ? (
          <div className="p-6 border-t border-white/20">
            <div className="flex justify-between items-center">
              <button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="bg-blue-600/50 hover:bg-blue-700/50 text-white px-4 py-2 rounded-lg text-sm font-medium transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('common.previous')}
              </button>
              <span className="text-blue-200">
                {t('admin.inviteBowlers.pageInfo', { current: currentPage, total: pagination.totalPages })}
              </span>
              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === pagination.totalPages}
                className="bg-blue-600/50 hover:bg-blue-700/50 text-white px-4 py-2 rounded-lg text-sm font-medium transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('common.next')}
              </button>
            </div>
          </div>
        ) : activeTab === 'proposeTeam' ? (
          <div className="p-6 border-t border-white/20">
            <div className="flex justify-between items-center">
              <div className="text-blue-200 text-sm">
                {selectedBowlers.length > 0 ? (
                  <>
                    <span className="font-medium">{t('admin.inviteBowlers.bowlerSelected', { count: selectedBowlers.length })}</span>
                    {selectedBowlers.length <= 3 && (
                      <span className="ml-2 text-blue-300">
                        ({selectedBowlers.map(b => b.name).join(', ')})
                      </span>
                    )}
                  </>
                ) : (
                  <span>{t('admin.inviteBowlers.selectBowlersPrompt')}</span>
                )}
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={handleProposeTeam}
                  disabled={proposing || forceRegistering || selectedBowlers.length === 0 || !teamName.trim()}
                  className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-6 py-2 rounded-lg font-medium transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  {proposing ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>{t('admin.inviteBowlers.proposing')}</span>
                    </>
                  ) : (
                    <>
                      <span>👥</span>
                      <span>{t('admin.inviteBowlers.proposeTeamButton')}</span>
                    </>
                  )}
                </button>
                <button
                  onClick={handleForceRegisterTeam}
                  disabled={proposing || forceRegistering || selectedBowlers.length === 0 || !teamName.trim()}
                  className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white px-6 py-2 rounded-lg font-medium transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  {forceRegistering ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>{t('admin.inviteBowlers.forceRegistering')}</span>
                    </>
                  ) : (
                    <>
                      <span>✅</span>
                      <span>{t('admin.inviteBowlers.forceRegisterTeamButton')}</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>

      {/* Add Bowler Modal */}
      {showAddBowlerModal && (
        <AddBowlerModalAdmin
          onClose={() => setShowAddBowlerModal(false)}
          onCreated={() => {
            setShowAddBowlerModal(false);
            fetchBowlers();
          }}
        />
      )}
    </div>
  );
};

export default InviteBowlersModal;
